import type { Image } from '@frontend/libs/types/media'

import Fancybox from '@frontend/components/fancybox/Fancybox'
import { cn } from '@nextui-org/react'
import { __ } from '@wordpress/i18n'
import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/navigation'
import { FreeMode, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import type { DeviceSpecificValue } from './lib/type'

import GalleryImage from './GalleryImage'

export interface GalleryCarouselProps {
  columns: DeviceSpecificValue
  enableLightbox: boolean
  gaps: DeviceSpecificValue
  images: Image[]
  showNavigation?: boolean
  showPagination?: boolean
}

export function GalleryCarousel({
  columns,
  enableLightbox,
  gaps,
  images,
  showNavigation = true,
  showPagination = true,
}: Readonly<GalleryCarouselProps>) {
  const renderSwiperSlide = (image: Image, isLightboxEnabled: boolean) => (
    <SwiperSlide key={image.id}>
      <GalleryImage image={image} isLightboxEnabled={isLightboxEnabled} />
    </SwiperSlide>
  )

  const renderSwiper = (isLightboxEnabled: boolean) => (
    <Swiper
      breakpoints={{
        480: {
          slidesPerView: columns.tablet,
          spaceBetween: gaps.tablet,
        },
        786: {
          slidesPerView: columns.desktop,
          spaceBetween: gaps.desktop,
        },
      }}
      freeMode={{
        enabled: true,
        sticky: true,
      }}
      loop={true}
      modules={[Pagination, Navigation, FreeMode]}
      navigation={{
        enabled: showNavigation,
        nextEl: '.swiper-button-custom-next',
        prevEl: '.swiper-button-custom-prev',
      }}
      pagination={{
        el: '.swiper-pagination-custom',
        enabled: showPagination,
        type: 'fraction',
      }}
      slidesPerView={columns.mobile}
      spaceBetween={gaps.mobile}
    >
      {images.map((image) => renderSwiperSlide(image, isLightboxEnabled))}

      <div slot="container-end">
        <div
          className={cn(
            `relative mt-10 flex w-full justify-center gap-16 font-medium`,
            {
              hidden: !showPagination && !showNavigation,
            },
          )}
        >
          {showNavigation && (
            <div className="swiper-button-custom-prev cursor-pointer select-none">
              {__('PREV', 'asavagroup')}
            </div>
          )}
          {showPagination && (
            <div className="swiper-pagination-custom w-auto"></div>
          )}
          {showNavigation && (
            <div className="swiper-button-custom-next cursor-pointer select-none">
              {__('NEXT', 'asavagroup')}
            </div>
          )}
        </div>
      </div>
    </Swiper>
  )

  return enableLightbox ? (
    <Fancybox
      options={{
        Carousel: {
          infinite: false,
        },
      }}
    >
      {renderSwiper(true)}
    </Fancybox>
  ) : (
    renderSwiper(false)
  )
}

export default GalleryCarousel
